import React, { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import {
  Box,
  Stack,
  Typography,
  Unstable_Grid2 as Grid,
  useTheme,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import { getMaterialColor } from '../../helpers';
import { useVocabulary, VocabularyKind } from '../../i18n';
import { ColorDot } from '../design-system';
import { PreviewMap } from '../PreviewMap';
import type {
  AmcrFindPreviewValues,
  AmcrVocabularies,
  AmcrVocabularyEntry,
} from './AmcrFindPreview';
import { toPoint } from './coordinates';
import { amcrFieldLabels } from './messages';

interface AmcrFindSummaryProps {
  control: Control<AmcrFindPreviewValues>;
  vocabularies: AmcrVocabularies;
}

const WATCHED_FIELDS = [
  'type',
  'material',
  'era',
  'context',
  'depth',
  'foundOn',
  'finderName',
  'localization',
  'latitude',
  'longitude',
] as const;

const titleStyles = { alignItems: 'center' };
const warningStyles = { alignItems: 'flex-start', color: 'warning.main' };
const warningIconStyles = { fontSize: 18, mt: '1px' };
const mapStyles = {
  width: '100%',
  aspectRatio: '16 / 9',
  overflow: 'hidden',
  borderRadius: 1.5,
  bgcolor: 'sunken',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const noMapStyles = { color: 'text.secondary' };

const lowerFirst = (value: string) =>
  value.charAt(0).toLocaleLowerCase() + value.slice(1);

const toCssColor = ([red, green, blue]: [number, number, number]) =>
  `rgb(${red}, ${green}, ${blue})`;

/** Overview of the record being exported: what it is, gaps, and where. */
const AmcrFindSummary = ({ control, vocabularies }: AmcrFindSummaryProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const vocabulary = useVocabulary();
  const [
    type,
    material,
    era,
    context,
    depth,
    foundOn,
    finderName,
    localization,
    latitude,
    longitude,
  ] = useWatch({ control, name: WATCHED_FIELDS });

  const keysById = useMemo(
    () =>
      new Map(
        [
          ...vocabularies.types,
          ...vocabularies.materials,
          ...vocabularies.eras,
        ].map((option) => [option.id, option.key])
      ),
    [vocabularies]
  );

  // Acolyte's translation of the AMCR entry; AMCR's own label as a fallback.
  const getLabel = (kind: VocabularyKind, entry?: AmcrVocabularyEntry) =>
    (entry && vocabulary(kind, keysById.get(entry.id))) || entry?.label || '';

  const materialKey = material ? keysById.get(material.id) : undefined;
  const markerColor = materialKey
    ? toCssColor(getMaterialColor(materialKey))
    : theme.palette.primary.main;
  const point = toPoint(latitude, longitude);

  const title = [
    getLabel('material', material),
    lowerFirst(getLabel('findType', type)),
  ]
    .filter(Boolean)
    .join(' ');
  const eraLabel = lowerFirst(getLabel('era', era));

  const missingFields: Array<[boolean, MessageDescriptor]> = [
    [!type?.id, amcrFieldLabels.type],
    [!material?.id, amcrFieldLabels.material],
    [!era?.id, amcrFieldLabels.era],
    [!context?.id, amcrFieldLabels.context],
    [!depth.trim(), amcrFieldLabels.depth],
    [!foundOn?.isValid, amcrFieldLabels.foundOn],
    [!finderName.trim(), amcrFieldLabels.finder],
    [!localization.trim(), amcrFieldLabels.localization],
    [!point, amcrFieldLabels.coordinates],
  ];
  const missingLabels = missingFields
    .filter(([isMissing]) => isMissing)
    .map(([, label]) => intl.formatMessage(label));
  const missingValues = { fields: missingLabels.join(', ') };

  return (
    <Grid container spacing={2}>
      <Grid xs={12} sm={9}>
        <Stack gap={1}>
          <Stack direction="row" gap={1} sx={titleStyles}>
            <ColorDot color={markerColor} size={12} />
            <Typography variant="subtitle1">
              {eraLabel ? `${title}, ${eraLabel}` : title}
            </Typography>
          </Stack>
          {missingLabels.length > 0 && (
            <Stack direction="row" gap={0.75} sx={warningStyles}>
              <WarningAmberRoundedIcon sx={warningIconStyles} />
              <Typography variant="body2">
                <FormattedMessage
                  id="amcrPas.preview.missingValues"
                  defaultMessage="Missing values: {fields}"
                  values={missingValues}
                />
              </Typography>
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid xs={12} sm={3}>
        <Box sx={mapStyles}>
          {point ? (
            <PreviewMap
              interactive={false}
              latitude={point.latitude}
              longitude={point.longitude}
              markerColor={markerColor}
            />
          ) : (
            <Typography variant="body2" sx={noMapStyles}>
              <FormattedMessage
                id="amcrPas.preview.noCoordinates"
                defaultMessage="No coordinates"
              />
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export { AmcrFindSummary };
